/**
 * Audit Filters Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Filter controls for audit log queries.
 */

import React, { useState, useEffect } from 'react';
import { Search, Calendar, Filter, X, ChevronDown } from 'lucide-react';
import { auditService } from '../../../services/admin';
import type { AuditFilter, AuditSeverity, AuditEventTypesResponse } from '../../../types/admin';
import { SEVERITY_LABELS } from '../../../types/admin';
import { Button } from '../../ui/button';

interface AuditFiltersProps {
  filter: AuditFilter;
  onFilterChange: (filter: AuditFilter) => void;
  onClear: () => void;
}

const SEVERITIES: AuditSeverity[] = ['debug', 'info', 'warning', 'error', 'critical'];

const AuditFilters: React.FC<AuditFiltersProps> = ({
  filter,
  onFilterChange,
  onClear,
}) => {
  const [eventTypes, setEventTypes] = useState<AuditEventTypesResponse | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Load event types
  useEffect(() => {
    auditService
      .getEventTypes()
      .then(setEventTypes)
      .catch((err) => console.error('Failed to load event types:', err));
  }, []);

  const datePresets = auditService.getDateRangePresets();

  const applyDatePreset = (days: number) => {
    const range = auditService.applyDateRangePreset(days);
    onFilterChange({
      ...filter,
      date_from: range.date_from,
      date_to: range.date_to,
    });
  };

  const hasActiveFilters =
    filter.search ||
    filter.event_type ||
    filter.severity ||
    filter.date_from ||
    filter.date_to ||
    filter.user_id ||
    filter.resource_type;

  const activeFilterCount = [
    filter.search,
    filter.event_type,
    filter.severity,
    filter.date_from || filter.date_to,
    filter.user_id,
    filter.resource_type,
  ].filter(Boolean).length;

  // Get event types for selected category
  const categoryEventTypes = selectedCategory && eventTypes?.categories[selectedCategory]
    ? eventTypes.categories[selectedCategory]
    : [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Search and quick filters */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={filter.search || ''}
            onChange={(e) => onFilterChange({ ...filter, search: e.target.value, page: 1 })}
            placeholder="Search logs..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
          />
        </div>

        {/* Date presets */}
        <div className="flex items-center gap-1">
          {datePresets.slice(0, 3).map((preset) => (
            <button
              key={preset.days}
              onClick={() => applyDatePreset(preset.days)}
              className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Severity filter */}
        <select
          value={filter.severity || ''}
          onChange={(e) =>
            onFilterChange({
              ...filter,
              severity: (e.target.value as AuditSeverity) || undefined,
              page: 1,
            })
          }
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
        >
          <option value="">All Severities</option>
          {SEVERITIES.map((sev) => (
            <option key={sev} value={sev}>
              {SEVERITY_LABELS[sev]}
            </option>
          ))}
        </select>

        {/* Expand/collapse */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="relative"
        >
          <Filter size={16} className="mr-1" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 bg-[#002B45] text-white text-xs px-1.5 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            size={14}
            className={`ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </Button>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X size={14} className="mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Expanded filters */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date from */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Date From
            </label>
            <div className="relative">
              <Calendar
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="datetime-local"
                value={filter.date_from ? filter.date_from.slice(0, 16) : ''}
                onChange={(e) =>
                  onFilterChange({
                    ...filter,
                    date_from: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                    page: 1,
                  })
                }
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              />
            </div>
          </div>

          {/* Date to */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Date To
            </label>
            <div className="relative">
              <Calendar
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="datetime-local"
                value={filter.date_to ? filter.date_to.slice(0, 16) : ''}
                onChange={(e) =>
                  onFilterChange({
                    ...filter,
                    date_to: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                    page: 1,
                  })
                }
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              />
            </div>
          </div>

          {/* Event category */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Event Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                onFilterChange({ ...filter, event_type: undefined, page: 1 });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            >
              <option value="">All Categories</option>
              {eventTypes &&
                Object.keys(eventTypes.categories).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
            </select>
          </div>

          {/* Event type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Event Type
            </label>
            <select
              value={filter.event_type || ''}
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  event_type: e.target.value || undefined,
                  page: 1,
                })
              }
              disabled={!selectedCategory}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] disabled:bg-gray-50"
            >
              <option value="">
                {selectedCategory ? 'All Types' : 'Select category first'}
              </option>
              {categoryEventTypes.map((type) => (
                <option key={type} value={type}>
                  {auditService.formatEventType(type)}
                </option>
              ))}
            </select>
          </div>

          {/* Resource type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Resource Type
            </label>
            <input
              type="text"
              value={filter.resource_type || ''}
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  resource_type: e.target.value || undefined,
                  page: 1,
                })
              }
              placeholder="e.g., user, client, team"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>

          {/* User ID */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              User ID
            </label>
            <input
              type="text"
              value={filter.user_id || ''}
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  user_id: e.target.value || undefined,
                  page: 1,
                })
              }
              placeholder="Filter by user ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>

          {/* IP Address */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              IP Address
            </label>
            <input
              type="text"
              value={filter.ip_address || ''}
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  ip_address: e.target.value || undefined,
                  page: 1,
                })
              }
              placeholder="e.g., 192.168.1.1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>

          {/* More date presets */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Quick Date Range
            </label>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  applyDatePreset(parseInt(e.target.value, 10));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              value=""
            >
              <option value="">Select preset...</option>
              {datePresets.map((preset) => (
                <option key={preset.days} value={preset.days}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditFilters;
